import React, { useEffect, useState } from "react";
import axiosInstance from "../../../utils/axiosInstance";

const getDefaultFormData = () => ({
  name: "",
  url: "",
  plan: "",
  clientName: "",
  platform: "",
  purchasedOn: new Date().toISOString().split("T")[0],
  expireDate: "",
  actualAmount: "",
  paidAmount: "",
});

const toViewDomain = (item) => ({
  id: item._id || item.id,
  name: item.name || "-",
  url: item.url || "-",
  plan: item.plan || "-",
  clientName: item.clientName || "-",
  platform: item.platform || "-",
  purchasedOn: item.purchasedOn || null,
  expireDate: item.expireDate || null,
  actualAmount: item.actualAmount || 0,
  paidAmount: item.paidAmount || 0,
  productType: item.productType || "domain",
  status:
    item.status === "Paid" || item.status === "Unpaid"
      ? item.status
      : item.status === "Approved"
      ? "Paid"
      : "Unpaid",
});

import "./Domain.css";

function Domain({ type = "domain" }) {
  const [domains, setDomains] = useState([]);
  const [previewDomain, setPreviewDomain] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formType, setFormType] = useState(type);
  const [formData, setFormData] = useState(getDefaultFormData());
  const [editingDomainId, setEditingDomainId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const fetchDomains = async () => {
    try {
      setIsLoading(true);
      setError("");
      const response = await axiosInstance.get("/api/domains");
      const list = Array.isArray(response.data) ? response.data : [];
      setDomains(list.map(toViewDomain));
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to fetch domains");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDomains();
    setShowForm(false);
    setPreviewDomain(null);
    setEditingDomainId(null);
    setError("");
  }, [type]);

  const handleOpenAddForm = (type = "domain") => {
    setFormType(type);
    setFormData(getDefaultFormData());
    setEditingDomainId(null);
    setShowForm(true);
  };

  const handleCloseAddForm = () => {
    setShowForm(false);
    setError("");
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCreateDomain = async (e) => {
    e.preventDefault();

    try {
      setIsSubmitting(true);
      setError("");
      const payload = {
        name: formData.name.trim(),
        url: formData.url.trim(),
        plan: formData.plan.trim(),
        clientName: formData.clientName.trim(),
        platform: formData.platform.trim(),
        purchasedOn: `${formData.purchasedOn}T00:00:00.000Z`,
        expireDate: `${formData.expireDate}T00:00:00.000Z`,
        actualAmount: Number(formData.actualAmount),
        paidAmount: Number(formData.paidAmount),
        productType: formType,
      };

      if (editingDomainId) {
        await axiosInstance.put(`/api/domains/${editingDomainId}`, payload);
      } else {
        await axiosInstance.post("/api/domains", payload);
      }

      handleCloseAddForm();
      setEditingDomainId(null);
      fetchDomains();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          (editingDomainId ? "Failed to update domain" : "Failed to create domain")
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenEditForm = (domain) => {
    const toInputDate = (value) => {
      if (!value) return "";
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return "";
      return d.toISOString().split("T")[0];
    };

    setFormData({
      name: domain.name === "-" ? "" : domain.name,
      url: domain.url === "-" ? "" : domain.url,
      plan: domain.plan === "-" ? "" : domain.plan,
      clientName: domain.clientName === "-" ? "" : domain.clientName,
      platform: domain.platform === "-" ? "" : domain.platform,
      purchasedOn: toInputDate(domain.purchasedOn),
      expireDate: toInputDate(domain.expireDate),
      actualAmount: domain.actualAmount || "",
      paidAmount: domain.paidAmount || "",
    });
    setFormType(domain.productType || "domain");
    setEditingDomainId(domain.id);
    setShowForm(true);
    setError("");
  };

  const handleStatusChange = async (id, value) => {
    try {
      await axiosInstance.put(`/api/domains/${id}/status`, { status: value });
      setDomains((prev) =>
        prev.map((domain) => (domain.id === id ? { ...domain, status: value } : domain))
      );
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to update status");
    }
  };

  const handleDeleteDomain = async (id) => {
    try {
      await axiosInstance.delete(`/api/domains/${id}`);
      setDomains((prev) => prev.filter((domain) => domain.id !== id));
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to delete domain");
    }
  };

  const handleDownloadDomain = (domain) => {
    const capitalizedType = domain.productType ? (domain.productType.charAt(0).toUpperCase() + domain.productType.slice(1)) : 'Domain';
    const lines = [
      `${capitalizedType} Name: ${domain.name || "-"}`,
      `Client Name: ${domain.clientName || "-"}`,
    ];
    if (domain.productType !== 'email') {
      lines.push(`${capitalizedType} URL: ${domain.url || "-"}`);
      lines.push(`Platform: ${domain.platform || "-"}`);
    }
    lines.push(
      `Plan: ${domain.plan || "-"}`,
      `Purchased On: ${formatCreatedDate(domain.purchasedOn)}`,
      `Expire Date: ${formatCreatedDate(domain.expireDate)}`,
      `Actual Amount: ${domain.actualAmount || 0}`,
      `Paid Amount: ${domain.paidAmount || 0}`,
      `Status: ${domain.status || "-"}`,
    );
    const fileContent = lines.join("\n");
    const blob = new Blob([fileContent], { type: "text/plain;charset=utf-8" });
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const safeName = String(domain.name || "domain")
      .trim()
      .replace(/[^a-z0-9-_]+/gi, "-")
      .replace(/^-+|-+$/g, "")
      .toLowerCase();

    link.href = objectUrl;
    link.download = `${safeName || domain.productType || "domain"}-details.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);
  };

  const handlePreviewDomain = (domain) => {
    setPreviewDomain(domain);
  };

  const formatCreatedDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleDateString("en-IN");
  };

  const filteredDomains = domains.filter((domain) =>
    [domain.name, domain.clientName, domain.url, domain.plan]
      .some((val) => val && val.toString().toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const domainsList = filteredDomains.filter(d => d.productType === 'domain');
  const emailsList = filteredDomains.filter(d => d.productType === 'email');
  const hostingsList = filteredDomains.filter(d => d.productType === 'hosting');

  const renderTable = (list, title) => (
    <div className="domain-section">
      <h3 className="section-title">{title}</h3>
      <div className="domain-table-wrap">
        <div className="domain-table-scroll">
          <table className="domain-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Client Name</th>
                <th>URL</th>
                <th>Plan</th>
                <th>Purchased On</th>
                <th>Expire Date</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {!isLoading && list.length === 0 && (
                <tr>
                  <td colSpan={8} style={{ padding: "40px 10px", textAlign: "center", color: "#64748b" }}>
                    No {title.toLowerCase()} found matching your criteria.
                  </td>
                </tr>
              )}
              {list.map((domain) => (
                <tr key={domain.id}>
                  <td>{domain.name}</td>
                  <td>{domain.clientName}</td>
                  <td>{domain.url === "-" ? "" : domain.url}</td>
                  <td>{domain.plan}</td>
                  <td>{formatCreatedDate(domain.purchasedOn)}</td>
                  <td>{formatCreatedDate(domain.expireDate)}</td>
                  <td>
                    <select
                      className="domain-select"
                      value={domain.status}
                      onChange={(e) => handleStatusChange(domain.id, e.target.value)}
                    >
                      <option value="Paid">Paid</option>
                      <option value="Unpaid">Unpaid</option>
                    </select>
                  </td>
                  <td>
                    <div className="domain-actions">
                      <button className="domain-btn domain-btn-view" type="button" onClick={() => handlePreviewDomain(domain)}>Preview</button>
                      <button className="domain-btn" type="button" onClick={() => handleDownloadDomain(domain)}>Download</button>
                      <button className="domain-btn domain-btn-edit" type="button" onClick={() => handleOpenEditForm(domain)}>Edit</button>
                      <button className="domain-btn domain-btn-delete" type="button" onClick={() => handleDeleteDomain(domain.id)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  return (
    <div className="domain-page">
      <div className="domain-hero">
        <div>
          <p className="domain-subtitle">
            {type === 'hosting' ? 'Admin Hosting Portal' : type === 'email' ? 'Admin Email Portal' : 'Admin Domain Portal'}
          </p>
          <h2 className="domain-title">
            {type === 'hosting' ? 'Hostings' : type === 'email' ? 'Emails' : 'Domains'}
          </h2>
          <p className="domain-caption">
            Track and manage client {type === 'hosting' ? 'hostings' : type === 'email' ? 'emails' : 'domains'}, plans, and renewals
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            className="domain-add-btn"
            type="button"
            onClick={() => handleOpenAddForm(type)}
          >
            Add {type.charAt(0).toUpperCase() + type.slice(1)}
          </button>
        </div>
      </div>

      {error && <div className="domain-error">{error}</div>}

      <div className="domain-search-wrap">
        <input
          type="text"
          className="domain-input domain-search-input"
          placeholder={type === 'email' ? "Search by name, client, or plan..." : "Search by name, client, url or plan..."}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <div className="domain-sections-wrap">
        {type === "domain" && renderTable(domainsList, "Domains")}
        {type === "hosting" && renderTable(hostingsList, "Hostings")}
        {type === "email" && renderTable(emailsList, "Emails")}
      </div>

      {showForm && (
        <div className="domain-modal-overlay">
          <form className="domain-modal" onSubmit={handleCreateDomain}>
            <h3>{editingDomainId ? `Edit ${formType.charAt(0).toUpperCase() + formType.slice(1)}` : `Add New ${formType.charAt(0).toUpperCase() + formType.slice(1)}`}</h3>

            <div className="domain-form-grid">
              <div className="domain-input-group">
                <span className="domain-input-label">{formType === 'hosting' ? 'Hosting Name' : formType === 'email' ? 'Email Name' : 'Domain Name'}</span>
                <input
                  type="text"
                  placeholder="e.g. example.com"
                  className="domain-input"
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  required
                />
              </div>
              <div className="domain-input-group">
                <span className="domain-input-label">Client Name</span>
                <input
                  type="text"
                  placeholder="e.g. John Doe"
                  className="domain-input"
                  value={formData.clientName}
                  onChange={(e) => handleInputChange("clientName", e.target.value)}
                  required
                />
              </div>
              {formType !== 'email' && (
                <>
                  <div className="domain-input-group">
                    <span className="domain-input-label">{formType === 'hosting' ? 'Hosting URL' : 'Domain URL'}</span>
                    <input
                      type="url"
                      placeholder="https://..."
                      className="domain-input"
                      value={formData.url}
                      onChange={(e) => handleInputChange("url", e.target.value)}
                      required
                    />
                  </div>
                  <div className="domain-input-group">
                    <span className="domain-input-label">Platform</span>
                    <input
                      type="text"
                      placeholder="e.g. GoDaddy"
                      className="domain-input"
                      value={formData.platform}
                      onChange={(e) => handleInputChange("platform", e.target.value)}
                      required
                    />
                  </div>
                </>
              )}
              <div className="domain-input-group">
                <span className="domain-input-label">Subscription Plan</span>
                <select
                  className="domain-input"
                  value={formData.plan}
                  onChange={(e) => handleInputChange("plan", e.target.value)}
                  required
                >
                  <option value="" disabled>Select Plan</option>
                  <option value="Monthly (1 month)">Monthly (1 month)</option>
                  <option value="Yearly (12 months)">Yearly (12 months)</option>
                  <option value="2 years (24 months)">2 years (24 months)</option>
                  <option value="4 years (48 months)">4 years (48 months)</option>
                </select>
              </div>
              <div className="domain-input-group">
                <span className="domain-input-label">Purchased Date</span>
                <input
                  type="date"
                  className="domain-input"
                  value={formData.purchasedOn}
                  onChange={(e) => handleInputChange("purchasedOn", e.target.value)}
                  required
                />
              </div>
              <div className="domain-input-group">
                <span className="domain-input-label">Expiry Date</span>
                <input
                  type="date"
                  className="domain-input"
                  value={formData.expireDate}
                  onChange={(e) => handleInputChange("expireDate", e.target.value)}
                  required
                />
              </div>
              <div className="domain-input-group">
                <span className="domain-input-label">Actual Amount (₹)</span>
                <input
                  type="number"
                  placeholder="0.00"
                  className="domain-input"
                  value={formData.actualAmount}
                  onChange={(e) => handleInputChange("actualAmount", e.target.value)}
                  required
                />
              </div>
              <div className="domain-input-group">
                <span className="domain-input-label">Paid Amount (₹)</span>
                <input
                  type="number"
                  placeholder="0.00"
                  className="domain-input"
                  value={formData.paidAmount}
                  onChange={(e) => handleInputChange("paidAmount", e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="domain-modal-actions">
              <button
                className="domain-btn-cancel"
                type="button"
                onClick={handleCloseAddForm}
              >
                Cancel
              </button>
              <button
                className="domain-btn-save"
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Processing..." : editingDomainId ? `Update ${formType.charAt(0).toUpperCase() + formType.slice(1)}` : `Register ${formType.charAt(0).toUpperCase() + formType.slice(1)}`}
              </button>
            </div>
          </form>
        </div>
      )}

      {previewDomain && (
        <div className="domain-modal-overlay" onClick={() => setPreviewDomain(null)}>
          <div className="domain-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{previewDomain.productType ? (previewDomain.productType.charAt(0).toUpperCase() + previewDomain.productType.slice(1)) : 'Domain'} Details</h3>
            <div className="domain-preview-content">
              <div className="domain-preview-field">
                <span className="domain-preview-label">
                  {previewDomain.productType === 'hosting' ? 'Hosting' : previewDomain.productType === 'email' ? 'Email' : 'Domain'} Name:
                </span>
                <span className="domain-preview-value">{previewDomain.name}</span>
              </div>
              <div className="domain-preview-field">
                <span className="domain-preview-label">Client Name:</span>
                <span className="domain-preview-value">{previewDomain.clientName}</span>
              </div>
              {previewDomain.productType !== 'email' && (
                <>
                  <div className="domain-preview-field">
                    <span className="domain-preview-label">
                      {previewDomain.productType === 'hosting' ? 'Hosting' : 'Domain'} URL:
                    </span>
                    <span className="domain-preview-value">
                      <a href={previewDomain.url} target="_blank" rel="noreferrer" style={{ color: "#003e6d" }}>{previewDomain.url}</a>
                    </span>
                  </div>
                  <div className="domain-preview-field">
                    <span className="domain-preview-label">Platform:</span>
                    <span className="domain-preview-value">{previewDomain.platform}</span>
                  </div>
                </>
              )}
              <div className="domain-preview-field">
                <span className="domain-preview-label">Plan:</span>
                <span className="domain-preview-value">{previewDomain.plan}</span>
              </div>
              <div className="domain-preview-field">
                <span className="domain-preview-label">Purchased On:</span>
                <span className="domain-preview-value">{formatCreatedDate(previewDomain.purchasedOn)}</span>
              </div>
              <div className="domain-preview-field">
                <span className="domain-preview-label">Expire Date:</span>
                <span className="domain-preview-value">{formatCreatedDate(previewDomain.expireDate)}</span>
              </div>
              <div className="domain-preview-field">
                <span className="domain-preview-label">Actual Amount:</span>
                <span className="domain-preview-value">₹{previewDomain.actualAmount}</span>
              </div>
              <div className="domain-preview-field">
                <span className="domain-preview-label">Paid Amount:</span>
                <span className="domain-preview-value">₹{previewDomain.paidAmount}</span>
              </div>
              <div className="domain-preview-field">
                <span className="domain-preview-label">Status:</span>
                <span className="domain-preview-value" style={{ fontWeight: 700 }}>{previewDomain.status}</span>
              </div>
            </div>
            <div className="domain-modal-actions">
              <button
                className="domain-btn-cancel"
                type="button"
                onClick={() => setPreviewDomain(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


export default Domain;
